import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Mail, CheckCircle } from 'lucide-react';

interface ForgotPasswordFormProps {
  onBackToSignIn: () => void;
}

export const ForgotPasswordForm = ({ onBackToSignIn }: ForgotPasswordFormProps) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const { resetPassword } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: "Erreur",
        description: "Veuillez saisir votre email",
        variant: "destructive"
      });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({
        title: "Erreur",
        description: "Veuillez saisir un email valide",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await resetPassword(email);

      if (error) {
        toast({
          title: "Erreur",
          description: error.message,
          variant: "destructive"
        });
      } else {
        setEmailSent(true);
        toast({
          title: "Email envoyé",
          description: "Vérifiez votre boîte mail pour réinitialiser votre mot de passe"
        });
      }
    } catch (_error) {
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="w-full">
        {/* Logo */}
        <div className="flex items-center mb-8">
          <img
            src="/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"
            alt="Getproof Logo"
            className="h-10"
          />
        </div>

        {/* Success State */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <CheckCircle className="w-16 h-16 text-teal-500" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Email envoyé !
          </h1>
          <p className="text-gray-600 mb-8">
            Vérifiez votre boîte mail et suivez les instructions pour réinitialiser votre mot de passe
          </p>

          <Button
            onClick={onBackToSignIn}
            className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg"
          >
            Retour à la connexion
          </Button>
        </div>

        {/* Footer links */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex justify-center space-x-6 text-sm text-gray-500">
            <a href="#" className="hover:text-gray-700">Conditions générales</a>
            <a href="#" className="hover:text-gray-700">Politique de confidentialité</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Logo */}
      <div className="flex items-center mb-8">
        <img
          src="/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"
          alt="Getproof Logo"
          className="h-10"
        />
      </div>

      {/* Title */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Mot de passe oublié ?
        </h1>
        <p className="text-gray-600">
          Saisissez votre email pour recevoir un lien de réinitialisation
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Entrez votre email"
              className="pl-10 h-12 border-gray-300 focus:border-teal-500 focus:ring-teal-500"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg"
          disabled={isLoading}
        >
          {isLoading ? 'Envoi...' : 'Envoyer le lien'}
        </Button>

        <div className="text-center pt-4">
          <button
            type="button"
            onClick={onBackToSignIn}
            className="text-sm text-teal-600 hover:text-teal-700 hover:underline font-medium"
          >
            ← Retour à la connexion
          </button>
        </div>
      </form>

      {/* Footer links */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <div className="flex justify-center space-x-6 text-sm text-gray-500">
          <a href="#" className="hover:text-gray-700">Conditions générales</a>
          <a href="#" className="hover:text-gray-700">Politique de confidentialité</a>
        </div>
      </div>
    </div>
  );
};
